<!-- Register route: path: '/org/:sub_level/:id/unit-evaluation', component: UnitEvaluationForm -->
<template>
    <div class="uef-page">
        <div class="section pt-0 mt-0">
            <div class="listed-detail mt-1 page-title">
                <h3 class="text-center mal-text mt-1 custom-title">ശാഖാ മൂല്യനിർണയ ഫോം</h3>
                <p class="text-center text-muted small mb-0" v-if="unitName">{{ unitName }}</p>
            </div>
        </div>

        <div class="section text-center py-4" v-if="loading">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="text-muted small mt-2 mal-text">ലോഡ് ചെയ്യുന്നു...</p>
        </div>

        <!-- Score summary (hidden when unit can edit) -->
        <div class="section pt-0" v-if="!loading && hasSubmitted && scoreSummary && !canEditForm">
            <div class="card uef-score-card">
                <div class="card-body">
                    <div class="uef-score-header">
                        <ion-icon name="checkmark-circle" class="uef-score-icon"></ion-icon>
                        <span class="mal-text">സമർപ്പിച്ചു</span>
                    </div>
                    <div class="row g-2 mt-1">
                        <div class="col-6">
                            <div class="uef-stat-box">
                                <div class="uef-stat-label mal-text">ആകെ മാർക്ക്</div>
                                <div class="uef-stat-value">{{ scoreSummary.total_score }}</div>
                            </div>
                        </div>
                        <div class="col-6">
                            <div class="uef-stat-box">
                                <div class="uef-stat-label mal-text">കാറ്റഗറി</div>
                                <div class="uef-stat-value">
                                    <span class="uef-category-badge" :class="categoryBadgeClass">
                                        {{ scoreSummary.category }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <p class="uef-category-hint mal-text text-center mb-0 mt-2">
                        {{ scoreSummary.category_label }}
                    </p>
                    <p class="text-muted small text-center mb-0 mt-2" v-if="!canEditForm && hasSubmitted">
                        <ion-icon name="lock-closed-outline"></ion-icon>
                        എഡിറ്റ് ലോക്ക് ചെയ്തിരിക്കുന്നു. അഡ്മിൻ എഡിറ്റ് അനുവദിച്ചാൽ മാത്രം വീണ്ടും എഡിറ്റ് ചെയ്യാം.
                    </p>
                </div>
            </div>
        </div>

        <div class="section pt-0" v-if="!loading && canEditForm && hasSubmitted">
            <div class="alert alert-success mal-text text-center mb-0 py-2">
                <ion-icon name="create-outline"></ion-icon>
                അഡ്മിൻ എഡിറ്റ് അനുവദിച്ചു — ഫോം എഡിറ്റ് ചെയ്ത് വീണ്ടും സമർപ്പിക്കുക
            </div>
        </div>

        <div class="section" v-if="!loading && !canOwnUnit && !hasSubmitted">
            <p class="text-center text-muted mal-text mb-0">ഈ ശാഖ ഇതുവരെ ഫോം സമർപ്പിച്ചിട്ടില്ല</p>
            <p class="text-center text-muted small mal-text mb-0 mt-1" v-if="!isYouthWing">
                യൂത്ത് വിംഗ് അഡ്മിൻ മാത്രമേ സമർപ്പിക്കാൻ കഴിയൂ
            </p>
            <p class="text-center text-muted small mal-text mb-0 mt-1" v-else>
                നിങ്ങളുടെ ശാഖയുടെ ഫോം മാത്രമേ സമർപ്പിക്കാൻ കഴിയൂ
            </p>
        </div>

        <form @submit.prevent="submitForm" v-if="!loading" class="uef-form">
            <div class="section pt-0" v-for="section in formSections" :key="section.id">
                <div class="card uef-form-card">
                    <div class="card-header uef-card-header" v-if="showFieldInputs && !showReadOnlyAnswers">
                        <ion-icon :name="section.icon"></ion-icon>
                        <span class="mal-text">{{ section.title }}</span>
                    </div>
                    <div class="card-body" :class="{ 'pb-2': showReadOnlyAnswers }">
                        <h6 class="uef-section-title mal-text" v-if="showReadOnlyAnswers">
                            <ion-icon :name="section.icon"></ion-icon>
                            {{ section.title }}
                        </h6>
                        <template v-if="showFieldInputs && !showReadOnlyAnswers">
                        <div
                            class="form-group basic"
                            v-for="(field, fIdx) in section.fields"
                            :key="'ed-' + field.key"
                            :class="{ 'mb-0': fIdx === section.fields.length - 1 }"
                        >
                            <div class="input-wrapper">
                                <label class="label text-primary mal-text">
                                    {{ field.label }}
                                    <span class="text-danger" v-if="canSubmitForm">*</span>
                                </label>
                                <select
                                    v-if="field.type === 'select'"
                                    class="form-control custom-select"
                                    v-model="form[field.key]"
                                    :required="canSubmitForm"
                                >
                                    <option value="" disabled>തിരഞ്ഞെടുക്കുക</option>
                                    <option
                                        v-for="opt in field.options"
                                        :key="field.key + '-' + opt.value"
                                        :value="opt.value"
                                    >
                                        {{ opt.label }}
                                    </option>
                                </select>
                                <select
                                    v-else
                                    class="form-control custom-select"
                                    v-model="form[field.key]"
                                    :required="canSubmitForm"
                                >
                                    <option value="" disabled>തിരഞ്ഞെടുക്കുക</option>
                                    <option value="true">അതെ</option>
                                    <option value="false">ഇല്ല</option>
                                </select>
                            </div>
                        </div>
                        </template>
                        <ul v-else-if="showReadOnlyAnswers" class="listview simple-listview flush uef-readonly-list">
                            <li v-for="field in section.fields" :key="'ro-' + field.key">
                                <span class="mal-text">{{ field.label }}</span>
                                <span class="text-primary fw-medium">{{ displayValue(field) }}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <div class="section" v-if="message">
                <div class="alert alert-danger mal-text mb-0">{{ message }}</div>
            </div>

            <div class="section uef-submit-section" v-if="canSubmitForm">
                <button type="submit" class="btn btn-primary btn-block btn-lg uef-submit-btn" :disabled="submitting">
                    <span v-show="submitting" class="spinner-border spinner-border-sm me-1"></span>
                    <ion-icon v-show="!submitting" name="paper-plane-outline" class="me-1"></ion-icon>
                    {{ hasSubmitted ? 'വീണ്ടും സമർപ്പിക്കുക' : 'സമർപ്പിക്കുക' }}
                </button>
            </div>
        </form>

        <alert-model :type="alert_type" :message="alert_message" ref="alert_model"></alert-model>
    </div>
</template>

<script>
import UserService from "../../services/user.service";
import AlertModel from "../layouts/AlertModel.vue";
import moment from "moment";

const BOOLEAN_KEYS = [
    "has_wisdom_office",
    "youth_mandal_exec_from_branch",
    "youth_state_district_committee_from_branch",
];

const SECTION_CONFIG = [
    {
        id: "members",
        title: "സദസ്യർ",
        icon: "people-outline",
        keys: ["branch_youth_members", "town_youth_members", "active_youth_members", "org_members", "other_wing_members"],
    },
    {
        id: "qhls",
        title: "QHLS",
        icon: "book-outline",
        keys: ["branch_qhls", "qhls_participation"],
    },
    {
        id: "programs",
        title: "യോഗങ്ങൾ & പങ്കാളിത്തം",
        icon: "calendar-outline",
        keys: ["youth_meeting", "wisdom_day_participation"],
    },
    {
        id: "institutions",
        title: "പള്ളി & മദ്രസ",
        icon: "business-outline",
        keys: ["salafi_mosque", "madrasa_arrangement"],
    },
    {
        id: "activities",
        title: "പ്രോഗ്രാമുകൾ (കഴിഞ്ഞ 6 മാസം)",
        icon: "megaphone-outline",
        keys: ["public_meetings_6m", "neighborhood_family_meetings_6m", "treat_program_beneficiaries"],
    },
    {
        id: "other",
        title: "മറ്റ് വിവരങ്ങൾ",
        icon: "information-circle-outline",
        keys: BOOLEAN_KEYS,
    },
];

const DEFAULT_FORM_OPTIONS = {
    branch_youth_members: [
        { value: "0_10", label: "0-10" },
        { value: "10_20", label: "10-20" },
        { value: "20_30", label: "20-30" },
        { value: "30_plus", label: "30+" },
    ],
    town_youth_members: [
        { value: "0_5", label: "0-5" },
        { value: "5_10", label: "5-10" },
        { value: "10_20", label: "10-20" },
        { value: "20_plus", label: "20+" },
    ],
    active_youth_members: [
        { value: "0", label: "0" },
        { value: "1", label: "1" },
        { value: "2", label: "2" },
        { value: "3_5", label: "3-5" },
        { value: "5_plus", label: "5+" },
    ],
    org_members: [
        { value: "0_10", label: "0-10" },
        { value: "10_20", label: "10-20" },
        { value: "20_30", label: "20-30" },
        { value: "30_plus", label: "30+" },
    ],
    other_wing_members: [
        { value: "0_20", label: "0-20" },
        { value: "20_30", label: "20-30" },
        { value: "30_40", label: "30-40" },
        { value: "40_plus", label: "40+" },
    ],
    branch_qhls: [
        { value: "regular", label: "സ്ഥിരമായി നടക്കുന്നുണ്ട്" },
        { value: "occasional", label: "ഇടക്കിടക്ക് നടക്കുന്നുണ്ട്" },
        { value: "previously", label: "മുമ്പ് നടന്നിരുന്നു" },
        { value: "planned", label: "തുടങ്ങാൻ പ്ലാൻ ആയിട്ടുണ്ട്" },
        { value: "none", label: "ഇല്ല" },
    ],
    qhls_participation: [
        { value: "0_10", label: "0-10" },
        { value: "10_20", label: "10-20" },
        { value: "20_40", label: "20-40" },
        { value: "40_plus", label: "40+" },
    ],
    youth_meeting: [
        { value: "weekly", label: "എല്ലാ ആഴ്ചയും" },
        { value: "fortnightly", label: "രണ്ടാഴ്ചയിലൊരിക്കൽ" },
        { value: "monthly", label: "മാസത്തിൽ ഒരു തവണ" },
        { value: "never", label: "സ്വതന്ത്രമായി ഇത് വരെ ചേർന്നിട്ടില്ല" },
    ],
    wisdom_day_participation: [
        { value: "0_10", label: "0-10" },
        { value: "10_20", label: "10-20" },
        { value: "20_40", label: "20-40" },
        { value: "40_plus", label: "40+" },
    ],
    salafi_mosque: [
        { value: "wisdom", label: "വിസ്ഡം" },
        { value: "knk", label: "KNM" },
        { value: "markaz_dawa", label: "മർക്കസു ദഅവ" },
        { value: "others", label: "മറ്റുള്ളവർ" },
        { value: "none", label: "പള്ളികളില്ല" },
    ],
    public_meetings_6m: [
        { value: "0", label: "0" },
        { value: "1", label: "1" },
        { value: "2", label: "2" },
        { value: "3_plus", label: "3+" },
    ],
    neighborhood_family_meetings_6m: [
        { value: "0", label: "0" },
        { value: "1", label: "1" },
        { value: "2", label: "2" },
        { value: "3_plus", label: "3+" },
    ],
    madrasa_arrangement: [
        { value: "wisdom", label: "വിസ്ഡം" },
        { value: "knk", label: "KNM" },
        { value: "markaz_dawa", label: "മർക്കസുദഅവ" },
        { value: "others", label: "മറ്റുള്ളവർ" },
        { value: "none", label: "ഇല്ല" },
    ],
    treat_program_beneficiaries: [
        { value: "0", label: "0" },
        { value: "1", label: "1" },
        { value: "2", label: "2" },
        { value: "3", label: "3" },
        { value: "4_plus", label: "4+" },
    ],
};

export default {
    name: "UnitEvaluationForm",
    components: { AlertModel },
    data() {
        return {
            loading: true,
            submitting: false,
            message: "",
            alert_type: "",
            alert_message: "",
            unitName: "",
            unitId: null,
            isEditable: true,
            canSubmitFromApi: true,
            editEnabled: false,
            toggleEditLoading: false,
            submittedAt: null,
            scoreSummary: null,
            formMeta: { fields: {}, options: {} },
            form: {
                branch_youth_members: "",
                town_youth_members: "",
                active_youth_members: "",
                org_members: "",
                other_wing_members: "",
                branch_qhls: "",
                qhls_participation: "",
                youth_meeting: "",
                wisdom_day_participation: "",
                salafi_mosque: "",
                public_meetings_6m: "",
                neighborhood_family_meetings_6m: "",
                madrasa_arrangement: "",
                has_wisdom_office: "",
                youth_mandal_exec_from_branch: "",
                youth_state_district_committee_from_branch: "",
                treat_program_beneficiaries: "",
            },
        };
    },
    computed: {
        currentUser() {
            return this.$store.state.auth.user;
        },
        isYouthWing() {
            return this.currentUser && parseInt(this.currentUser.wing, 10) === 2;
        },
        isYouthUnitUser() {
            const unitId = parseInt(this.unitId, 10);
            const user = this.currentUser;
            if (!user || !unitId || parseInt(user.wing, 10) !== 2) {
                return false;
            }
            if (parseInt(user.unit_id, 10) === unitId) {
                return true;
            }
            const memberUnitId = parseInt(user.member_unit_id || user.member?.unit_id, 10);
            return memberUnitId > 0 && memberUnitId === unitId;
        },
        canOwnUnit() {
            return this.isYouthUnitUser || this.canSubmitFromApi === true;
        },
        hasSubmitted() {
            return !!(this.submittedAt || (this.scoreSummary && this.scoreSummary.submitted_at));
        },
        showFieldInputs() {
            return Object.keys(this.formMeta.fields || {}).length > 0;
        },
        showReadOnlyAnswers() {
            return this.hasSubmitted && !this.canEditForm;
        },
        canEditForm() {
            if (!this.canOwnUnit) {
                return false;
            }
            return !this.hasSubmitted || !!this.isEditable;
        },
        canSubmitForm() {
            return this.canEditForm && this.canSubmitFromApi === true;
        },
        categoryBadgeClass() {
            const c = this.scoreSummary?.category || "";
            return {
                A: "uef-cat-a",
                B: "uef-cat-b",
                C: "uef-cat-c",
                D: "uef-cat-d",
            }[c] || "uef-cat-d";
        },
        formSections() {
            return SECTION_CONFIG.map((section) => ({
                id: section.id,
                title: section.title,
                icon: section.icon,
                fields: section.keys.map((key) => this.buildField(key)),
            })).filter((s) => s.fields.length);
        },
    },
    mounted() {
        if (!this.currentUser) {
            location.replace("/");
            return;
        }
        this.unitId = parseInt(this.$route.params.id) || this.currentUser.unit_id;
        this.loadData();
        document.addEventListener("visibilitychange", this.onPageVisible);
    },
    beforeUnmount() {
        document.removeEventListener("visibilitychange", this.onPageVisible);
    },
    activated() {
        if (this.unitId) {
            this.loadData();
        }
    },
    watch: {
        "$route.params.id"(id) {
            this.unitId = parseInt(id) || this.currentUser.unit_id;
            this.loadData();
        },
    },
    methods: {
        onPageVisible() {
            if (document.visibilityState === "visible" && this.unitId) {
                this.loadData();
            }
        },
        syncEditState(data) {
            const editOn = !!(
                data.edit_enabled
                || data.evaluation?.edit_enabled
                || data.score_summary?.edit_enabled
            );
            this.editEnabled = editOn;
            if (typeof data.is_editable === "boolean") {
                this.isEditable = data.is_editable;
                return;
            }
            this.isEditable = !this.hasSubmitted || editOn;
        },
        normalizeOptionList(raw) {
            if (Array.isArray(raw)) {
                return raw
                    .map((item) => ({
                        value: item?.value ?? item?.id ?? item?.key,
                        label: item?.label ?? item?.name ?? String(item?.value ?? ""),
                    }))
                    .filter((item) => item.value !== undefined && item.value !== null && item.value !== "");
            }
            if (raw && typeof raw === "object") {
                return Object.keys(raw).map((value) => ({
                    value,
                    label: raw[value],
                }));
            }
            return [];
        },
        mergeFormMeta(meta) {
            const fields = meta?.fields || meta?.field_labels || {};
            const apiOptions = meta?.options || {};
            const options = { ...DEFAULT_FORM_OPTIONS };
            Object.keys(DEFAULT_FORM_OPTIONS).forEach((key) => {
                const normalized = this.normalizeOptionList(apiOptions[key]);
                if (normalized.length) {
                    options[key] = normalized;
                }
            });
            Object.keys(apiOptions).forEach((key) => {
                const normalized = this.normalizeOptionList(apiOptions[key]);
                if (normalized.length) {
                    options[key] = normalized;
                }
            });
            return { fields, options };
        },
        buildField(key) {
            const isBoolean = BOOLEAN_KEYS.includes(key);
            const options = isBoolean
                ? []
                : (this.normalizeOptionList(this.formMeta.options[key]).length
                    ? this.normalizeOptionList(this.formMeta.options[key])
                    : (DEFAULT_FORM_OPTIONS[key] || []));
            return {
                key,
                label: this.formMeta.fields[key] || key,
                type: isBoolean ? "boolean" : "select",
                options,
            };
        },
        displayValue(field) {
            const val = this.form[field.key];
            if (field.type === "boolean") {
                if (val === "true" || val === true) return "അതെ";
                if (val === "false" || val === false) return "ഇല്ല";
                return "—";
            }
            const opt = (field.options || []).find((o) => o.value === val);
            return opt ? opt.label : val || "—";
        },
        formatDate(date) {
            return date ? moment(date).format("DD-MM-YYYY") : "";
        },
        loadData() {
            this.loading = true;
            UserService.authGetRequest("unit-evaluations/form-meta").then((metaRes) => {
                if (metaRes.data?.status === "success" || metaRes.data?.data) {
                    const meta = metaRes.data.data || metaRes.data || {};
                    this.formMeta = this.mergeFormMeta(meta);
                } else {
                    this.formMeta = this.mergeFormMeta({});
                }
                return UserService.authGetRequest(`unit-evaluations/${this.unitId}`);
            }).then((res) => {
                if (res.data.status === "success") {
                    this.message = "";
                    const data = res.data.data;
                    this.unitName = data.unit?.name || "";
                    if (data.evaluation) {
                        this.populateForm(data.evaluation);
                        this.submittedAt = data.evaluation.submitted_at || null;
                    }
                    if (data.show_score && data.score_summary) {
                        this.scoreSummary = data.score_summary;
                        this.submittedAt = data.score_summary.submitted_at || this.submittedAt;
                    } else if (data.evaluation?.submitted_at) {
                        this.scoreSummary = {
                            total_score: data.evaluation.total_score,
                            category: data.evaluation.category,
                            category_label: data.score_summary?.category_label || "",
                            submitted_at: data.evaluation.submitted_at,
                            edit_enabled: !!data.evaluation.edit_enabled,
                        };
                    } else {
                        this.scoreSummary = null;
                    }
                    this.canSubmitFromApi = typeof data.can_submit === "boolean"
                        ? data.can_submit
                        : this.isYouthUnitUser;
                    this.syncEditState(data);
                }
                this.loading = false;
            }).catch(() => {
                this.loading = false;
                const metaLoaded = this.formMeta.fields && Object.keys(this.formMeta.fields).length > 0;
                if (metaLoaded && this.isYouthUnitUser) {
                    this.message = "";
                    this.isEditable = true;
                    this.canSubmitFromApi = true;
                } else if (!this.hasSubmitted) {
                    this.message = "ഡാറ്റ ലോഡ് ചെയ്യാൻ കഴിഞ്ഞില്ല";
                }
            });
        },
        populateForm(evaluation) {
            Object.keys(this.form).forEach((key) => {
                if (BOOLEAN_KEYS.includes(key)) {
                    this.form[key] = evaluation[key] ? "true" : "false";
                } else if (evaluation[key] !== null && evaluation[key] !== undefined) {
                    this.form[key] = evaluation[key];
                }
            });
        },
        buildPayload() {
            const payload = { unit_id: this.unitId };
            Object.keys(this.form).forEach((key) => {
                if (BOOLEAN_KEYS.includes(key)) {
                    payload[key] = this.form[key] === "true";
                } else {
                    payload[key] = this.form[key];
                }
            });
            return payload;
        },
        toggleEditForUnit() {
            this.toggleEditLoading = true;
            UserService.authPostRequest(`unit-evaluations/${this.unitId}/toggle-edit`, {}).then((res) => {
                this.toggleEditLoading = false;
                if (res.data.status === "success") {
                    if (this.scoreSummary) {
                        this.scoreSummary.edit_enabled = !!res.data.data.edit_enabled;
                    }
                    this.syncEditState({
                        is_editable: res.data.data.is_editable,
                        edit_enabled: res.data.data.edit_enabled,
                        evaluation: { edit_enabled: res.data.data.edit_enabled },
                        score_summary: this.scoreSummary,
                    });
                    this.alert_type = "success";
                    this.alert_message = res.data.message;
                    this.$refs.alert_model.show();
                } else {
                    this.message = res.data.message || "അപ്ഡേറ്റ് ചെയ്യാൻ കഴിഞ്ഞില്ല";
                }
            }).catch((err) => {
                this.toggleEditLoading = false;
                this.message = err.response?.data?.message || "അപ്ഡേറ്റ് ചെയ്യാൻ കഴിഞ്ഞില്ല";
            });
        },
        applySubmitSuccess(body) {
            const data = body.data || {};
            const summary = data.score_summary;
            this.message = "";
            if (summary) {
                this.scoreSummary = summary;
                this.submittedAt = summary.submitted_at || new Date().toISOString();
            }
            if (data.evaluation) {
                this.populateForm(data.evaluation);
            }
            this.syncEditState({
                is_editable: data.is_editable === true,
                edit_enabled: !!(data.edit_enabled ?? summary?.edit_enabled),
                evaluation: data.evaluation || { edit_enabled: summary?.edit_enabled },
                score_summary: summary,
            });
            this.alert_type = "success";
            this.alert_message = body.message || "ഫോം വിജയകരമായി സമർപ്പിച്ചു";
            this.$refs.alert_model?.show();
            window.scrollTo({ top: 0, behavior: "smooth" });
            this.loadData();
        },
        submitForm() {
            if (this.submitting) {
                return;
            }
            this.message = "";
            this.submitting = true;
            UserService.authPostRequest("unit-evaluations", this.buildPayload()).then((res) => {
                this.submitting = false;
                const body = res?.data || {};
                if (body.status === "success" || body.data?.score_summary) {
                    this.applySubmitSuccess(body);
                    return;
                }
                this.message = body.message || "സമർപ്പിക്കാൻ കഴിഞ്ഞില്ല";
            }).catch((err) => {
                this.submitting = false;
                const body = err?.response?.data;
                if (body && (body.status === "success" || body.data?.score_summary)) {
                    this.applySubmitSuccess(body);
                    return;
                }
                this.message = body?.message || "സമർപ്പിക്കാൻ കഴിഞ്ഞില്ല";
            });
        },
    },
};
</script>

<style scoped>
.uef-page {
    padding-bottom: 88px;
}

.custom-title {
    font-size: 1.05rem;
    font-weight: 600;
    color: #1a418c;
}

.uef-score-card {
    border: none;
    border-radius: 14px;
    box-shadow: 0 4px 16px rgba(26, 65, 140, 0.12);
    background: linear-gradient(145deg, #f8faff 0%, #ffffff 100%);
}

.uef-score-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-weight: 600;
    color: #1a418c;
    font-size: 1rem;
}

.uef-score-icon {
    font-size: 1.4rem;
    color: #28a745;
}

.uef-stat-box {
    background: #fff;
    border-radius: 10px;
    padding: 12px 8px;
    text-align: center;
    border: 1px solid #e8ecf4;
}

.uef-stat-label {
    font-size: 0.72rem;
    color: #6c757d;
    margin-bottom: 4px;
}

.uef-stat-value {
    font-size: 1.35rem;
    font-weight: 700;
    color: #1a418c;
    line-height: 1.2;
}

.uef-category-badge {
    display: inline-block;
    min-width: 36px;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 1.1rem;
    font-weight: 700;
    color: #fff;
}

.uef-cat-a { background: #198754; }
.uef-cat-b { background: #1a418c; }
.uef-cat-c { background: #fd7e14; }
.uef-cat-d { background: #dc3545; }

.uef-admin-card {
    border: none;
    border-radius: 14px;
    box-shadow: 0 4px 16px rgba(26, 65, 140, 0.12);
}

.uef-category-hint {
    font-size: 0.8rem;
    color: #6c757d;
}

.uef-form-card {
    border: none;
    border-radius: 12px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
    overflow: hidden;
}

.uef-card-header {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #f0f4fc;
    border-bottom: 1px solid #e8ecf4;
    padding: 12px 16px;
    font-weight: 600;
    font-size: 0.9rem;
    color: #1a418c;
}

.uef-card-header ion-icon {
    font-size: 1.15rem;
}

.uef-form-card .card-body {
    padding: 16px;
}

.uef-form-card .form-group.basic {
    margin-bottom: 16px;
}

.uef-form-card .form-group.basic:last-child {
    margin-bottom: 0;
}

.uef-form-card .label {
    font-size: 0.82rem;
    line-height: 1.35;
    margin-bottom: 6px;
}

.uef-form-card .form-control,
.uef-form-card .custom-select {
    border-radius: 10px;
    border-color: #d0d7e8;
    min-height: 44px;
    font-size: 0.95rem;
    pointer-events: auto;
    touch-action: manipulation;
    -webkit-appearance: menulist;
    appearance: menulist;
}

.uef-section {
    margin-bottom: 20px;
}

.uef-section:last-child {
    margin-bottom: 0;
}

.uef-section-title {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #1a418c;
    font-weight: 600;
    font-size: 0.88rem;
    margin-bottom: 10px;
    padding-bottom: 6px;
    border-bottom: 1px solid #e8ecf4;
}

.uef-readonly-list li {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid #f0f2f5;
    font-size: 0.85rem;
}

.uef-readonly-list li:last-child {
    border-bottom: none;
}

.uef-readonly-list li span:first-child {
    flex: 1;
    color: #495057;
    line-height: 1.35;
}

.uef-readonly-list li span:last-child {
    flex-shrink: 0;
    text-align: right;
    max-width: 45%;
}

.uef-submit-section {
    padding-bottom: 8px;
}

.uef-submit-btn {
    border-radius: 12px;
    min-height: 48px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(26, 65, 140, 0.25);
}
</style>
